import React, { useState } from 'react';
import Navigation from './common/Navigation';

interface WaitlistSignupProps {
  software_type: 'cyclechef' | 'htma' | 'practice_management';
  onSuccess: () => void;
}

const WaitlistSignup: React.FC<WaitlistSignupProps> = ({ software_type, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/software-waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          software_type,
          interest_level: 'high'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        onSuccess();
        setEmail('');
        setName('');
      } else {
        setError(data.error || 'Failed to join waitlist');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <input
        type="email"
        placeholder="Your email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-colors"
        required
      />
      <input
        type="text"
        placeholder="Your name (optional)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-colors"
      />
      {error && (
        <p className="text-red-600 text-sm">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading || !email}
        className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Joining...' : 'Join Waitlist'}
      </button>
    </form>
  );
};

const SoftwarePage: React.FC = () => {
  const [successMessage, setSuccessMessage] = useState('');
  const [showSignup, setShowSignup] = useState<string | null>(null);

  const handleSignupSuccess = () => {
    setSuccessMessage('🎉 You\'ve successfully joined the waitlist!');
    setShowSignup(null);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-green-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        {successMessage && (
          <div className="bg-green-100 border border-green-300 text-green-800 px-6 py-4 rounded-lg mb-8 text-center">
            {successMessage}
          </div>
        )}
        
        <div className="text-center mb-12">
          <p className="text-green-600 font-semibold mb-4">Digital Wellness Tools</p>
          <h1 className="text-5xl font-bold mb-6">
            Empower Your Health with{' '}
            <span className="text-green-600 italic">Technology</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Innovative software solutions designed to support your wellness journey with 
            data-driven insights and personalized guidance.
          </p>
        </div>

        <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <span className="text-2xl">🍽️</span>
            </div>
            <h2 className="text-2xl font-bold mb-4">CycleChef</h2>
            <p className="text-gray-600 mb-6">
              A revolutionary app that syncs your nutrition with your body's natural rhythms. 
              Plan meals, track symptoms, and optimize your diet based on your unique cycle patterns.
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Cycle-synced meal planning</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Personalized nutrition recommendations</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Symptom tracking and insights</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Recipe library tailored to your needs</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <a 
                href="https://www.cyclechef.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-700 font-semibold flex items-center"
              >
                Learn More & Join Waitlist →
              </a>
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                Coming Soon
              </span>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <span className="text-2xl">🔬</span>
            </div>
            <h2 className="text-2xl font-bold mb-4">HTMA Analysis Tool</h2>
            <p className="text-gray-600 mb-6">
              Advanced Hair Tissue Mineral Analysis software that provides comprehensive insights 
              into your mineral balance, heavy metal toxicity, and metabolic patterns.
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Detailed mineral ratio analysis</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Heavy metal assessment</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Personalized supplement protocols</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Progress tracking over time</span>
              </div>
            </div>
            <div className="space-y-3">
              {showSignup === 'htma' ? (
                <WaitlistSignup software_type="htma" onSuccess={handleSignupSuccess} />
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">More details coming soon</span>
                    <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                      In Development
                    </span>
                  </div>
                  <button
                    onClick={() => setShowSignup('htma')}
                    className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                    Join Waitlist
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
              <span className="text-2xl">👩‍⚕️</span>
            </div>
            <h2 className="text-2xl font-bold mb-4">Nutritionist Management Platform</h2>
            <p className="text-gray-600 mb-6">
              Complete practice management software for holistic nutritionists. Streamline 
              client intake, track progress, manage appointments, and deliver personalized care 
              all in one integrated platform.
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Comprehensive client intake forms</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Appointment scheduling & management</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Progress tracking & analytics</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span className="text-gray-700">Secure client portal & communications</span>
              </div>
            </div>
            <div className="space-y-3">
              {showSignup === 'practice_management' ? (
                <WaitlistSignup software_type="practice_management" onSuccess={handleSignupSuccess} />
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Practice management reimagined</span>
                    <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                      In Development
                    </span>
                  </div>
                  <button
                    onClick={() => setShowSignup('practice_management')}
                    className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                    Join Waitlist
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Stay Updated</h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Be the first to know when our software tools launch. Join our waitlist to receive 
            exclusive early access and special launch pricing.
          </p>
          {showSignup === 'general' ? (
            <div className="max-w-md mx-auto">
              <WaitlistSignup software_type="practice_management" onSuccess={handleSignupSuccess} />
            </div>
          ) : (
            <button 
              onClick={() => setShowSignup('general')}
              className="bg-green-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-green-700 transition-colors"
            >
              Join the Waitlist
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SoftwarePage;